import React, { useState } from "react";
import {
  Card, CardContent, Avatar, Chip, Box, Typography, IconButton, Button,
} from "@mui/material";
import FavoriteIcon      from "@mui/icons-material/Favorite";
import CommentIcon       from "@mui/icons-material/Comment";
import RepeatIcon        from "@mui/icons-material/Repeat";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import LinkIcon          from "@mui/icons-material/Link";
import ImageIcon         from "@mui/icons-material/Image";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon    from "@mui/icons-material/AccessTime";
import { Badge as AnalysisBadge, Chip as AnalysisChip } from "./primitives";
import { fmtShortDate } from "./utils";
import { renderPlatformIcon } from "../../Automated Crawler/components/PlatformIcon";

const font = "Host Grotesk, sans-serif";

// mirrors FeedItem's getPlatformColor — Avatar background
const getPlatformBg = (p) => ({
  twitter: "#ffffff", facebook: "#E9F1FB", instagram: "#FDECEF",
  threads: "#ffffff", youtube: "#FFEBEB", reddit: "#FF4500",
}[p] ?? "#64748b");

// mirrors FeedItem's getPlatformColorChip — platform Chip background
const getPlatformChipColor = (p) => ({
  twitter: "#1DA1F2", facebook: "#4267B2", instagram: "#E4405F",
  youtube: "#FF0000", reddit: "#FF4500",
}[p] ?? "#64748b");

const PREDICTION_STYLE = {
  negative: { bg: "rgba(239,68,68,0.1)",   color: "#DC2626", border: "rgba(239,68,68,0.25)",   label: "↓ Negative" },
  positive: { bg: "rgba(16,185,129,0.1)",  color: "#059669", border: "rgba(16,185,129,0.25)",  label: "↑ Positive" },
  neutral:  { bg: "rgba(100,116,139,0.1)", color: "#475569", border: "rgba(100,116,139,0.25)", label: "→ Neutral"  },
};
const SUBJECTIVITY_STYLE = {
  subjective: { bg: "rgba(245,158,11,0.1)",  color: "#D97706", border: "rgba(245,158,11,0.2)",  label: "Subjective" },
  objective:  { bg: "rgba(59,130,246,0.1)",  color: "#2563EB", border: "rgba(59,130,246,0.2)",  label: "Objective"  },
};
const SECTION_CHIP_STYLE = { bg: "rgba(239,68,68,0.08)", color: "#DC2626", border: "rgba(239,68,68,0.22)" };

const formatSection = (s) =>
  s.replace(/\(([IVXLCDM]+)\)/g, (_, r) => `(${r.toLowerCase()})`);

function AnalysisTag({ cfg, label, size = "sm" }) {
  if (!cfg) return null;
  const lg = size === "lg";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      fontSize: lg ? 13 : 10, fontWeight: 700,
      padding: lg ? "4px 11px" : "2px 7px",
      borderRadius: 20, letterSpacing: "0.2px",
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      fontFamily: "'Host Grotesk', sans-serif", whiteSpace: "nowrap",
    }}>
      {label ?? cfg.label}
    </span>
  );
}

export const PostCard = React.memo(function PostCard({ post, type, sessionType, isStreamed, platform }) {
  const [expanded,        setExpanded]        = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);

  const pKey      = (post.platform || platform || "").toLowerCase();
  const handle    = post.userHandle || post.postOwner || "Unknown User";
  const bodyText  = post.content || post.text || "";
  const isLong    = bodyText.length > 300;
  const shownText = isLong && !expanded ? bodyText.slice(0, 300) + "…" : bodyText;

  const translationText = post.translation ?? "";
  const hasTranslation  =
    translationText && translationText !== bodyText && post.languages !== "English";

  const isProcessed  = type === "processed";
  // raw → purple  |  processed → green  (streamed raw also stays purple)
  const accentColor  = isProcessed ? "#10B981" : "#8B5CF6";

  return (
    <Card
      sx={{
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        border: `1px solid ${isStreamed ? "rgba(139,92,246,0.3)" : "#e2e8f0"}`,
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
        transition: "all 0.3s ease",
        animation: "slideIn 0.28s ease both",
        position: "relative",
        overflow: "visible",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)",
        },
      }}
    >
      {/* Inner wrapper — clips accent bar to the card's border radius */}
      <Box sx={{ position: "relative", borderRadius: "16px", overflow: "hidden" }}>

      {/* Left accent bar */}
      <Box sx={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
        background: accentColor,
      }} />

      {/* Live dot — purple for raw stream */}
      {isStreamed && !isProcessed && (
        <Box sx={{
          position: "absolute", top: 16, right: 16,
          width: 6, height: 6, borderRadius: "50%", background: "#8B5CF6",
        }} />
      )}

      <CardContent sx={{ p: 3 }}>

        {/* ── Header ── */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Avatar sx={{ bgcolor: getPlatformBg(pKey), width: 48, height: 48, mr: 2, fontSize: "1.2rem" }}>
            {renderPlatformIcon(pKey || "unknown")}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b", fontFamily: font }}>
                {handle}
              </Typography>
              <Chip
                label={pKey || "unknown"}
                size="small"
                sx={{ bgcolor: getPlatformChipColor(pKey), color: "white", fontFamily: font, fontSize: "0.75rem", height: 20 }}
              />
              {/* PostCard-specific: processed badges OR keyword chip */}
              {isProcessed ? (
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                  {sessionType === "sentiment" && post.analysis ? (
                    <>
                      <AnalysisTag
                        cfg={PREDICTION_STYLE[(post.analysis.prediction || "").toLowerCase()]}
                        size="sm"
                      />
                      <AnalysisTag
                        cfg={SUBJECTIVITY_STYLE[(post.analysis.subjectivity || "").toLowerCase()]}
                        size="sm"
                      />
                    </>
                  ) : (
                    <>
                      <AnalysisBadge value={post.sentiment}     type="sentiment" />
                      <AnalysisBadge value={post.legality}      type="legality"  />
                      <AnalysisBadge value={post.severityLevel} type="severity"  />
                    </>
                  )}
                </Box>
              ) : post.keyword ? (
                <Chip
                  label={post.keyword}
                  size="small"
                  sx={{
                    fontSize: "0.7rem", fontFamily: font,
                    color: "#4F46E5", bgcolor: "rgba(99,102,241,0.07)",
                    border: "1px solid rgba(99,102,241,0.18)",
                  }}
                />
              ) : null}
            </Box>
            <Typography variant="caption" sx={{ color: "#64748b", fontFamily: font, display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: "0.8rem" }} />
              {fmtShortDate(post.datetime || post.crawlTime)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, flexShrink: 0, ml: 1 }}>
            <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: font }}>
              Post ID: {post._id?.slice(-8) || "—"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: "0.7rem", color: "#94a3b8" }} />
              <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: font }}>
                {fmtShortDate(post.crawlTime)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── Body text ── */}
        {bodyText ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ color: "#374151", fontFamily: font, lineHeight: 1.6 }}>
              {shownText}
              {isLong && (
                <Box
                  component="button"
                  onClick={() => setExpanded((v) => !v)}
                  sx={{ border: "none", background: "none", cursor: "pointer", fontSize: "0.75rem", fontWeight: 700, color: "#6366F1", fontFamily: font, p: 0, ml: 0.5 }}
                >
                  {expanded ? "less" : "more"}
                </Box>
              )}
            </Typography>
          </Box>
        ) : null}

        {/* ── Metadata blockquote ── */}
        {post.metadata && post.metadata !== bodyText && (
          <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "8px", borderLeft: "4px solid #3b82f6", mb: 2 }}>
            <Typography variant="body2" sx={{ color: "#64748b", fontFamily: font, fontStyle: "italic" }}>
              {post.metadata}
            </Typography>
          </Box>
        )}

        {/* ── Processed analysis strip (PostCard-specific) ── */}
        {isProcessed && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", p: 1.5, bgcolor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0", mb: 2 }}>
            {sessionType === "sentiment" && post.analysis ? (
              <>
                <AnalysisTag
                  cfg={PREDICTION_STYLE[(post.analysis.prediction || "").toLowerCase()]}
                  label={`Prediction: ${post.analysis.prediction || "—"}`}
                  size="lg"
                />
                <AnalysisTag
                  cfg={SUBJECTIVITY_STYLE[(post.analysis.subjectivity || "").toLowerCase()]}
                  label={`Subjectivity: ${post.analysis.subjectivity || "—"}`}
                  size="lg"
                />
              </>
            ) : (
              <>
                {post.offensiveScore    && <AnalysisChip>Offensive: {post.offensiveScore}</AnalysisChip>}
                {post.sentimentAccuracy && <AnalysisChip>Confidence: {post.sentimentAccuracy}</AnalysisChip>}
                {post.languageType      && <AnalysisBadge value={post.languageType} type="legality" />}
                {post.content_type      && <AnalysisChip>{post.content_type}</AnalysisChip>}
                {post.intensity         && <AnalysisBadge value={post.intensity} type="severity" />}
                {post.labels?.map((l)   => <AnalysisChip key={l} accent>{l}</AnalysisChip>)}
                {post.sections?.map((s) => (
                  <span key={s} style={{
                    fontSize: 13, fontWeight: 700, fontFamily: "'Host Grotesk', sans-serif",
                    padding: "4px 11px", borderRadius: 20,
                    background: SECTION_CHIP_STYLE.bg, color: SECTION_CHIP_STYLE.color,
                    border: `1px solid ${SECTION_CHIP_STYLE.border}`,
                    whiteSpace: "nowrap",
                  }}>{formatSection(s)}</span>
                ))}
              </>
            )}
          </Box>
        )}

        {/* ── Hashtags ── */}
        {post.hashtags?.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {post.hashtags.slice(0, 5).map((tag, i) => (
              <Chip key={i} label={tag} size="small" variant="outlined"
                sx={{ fontSize: "0.7rem", fontFamily: font, color: "#6366f1", borderColor: "#6366f1" }}
              />
            ))}
          </Box>
        )}

        {/* ── Links ── */}
        {(post.link || post.screenshotUrl) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {post.link && post.link !== "n/a" && (
              <Button variant="outlined" size="small" startIcon={<LinkIcon />}
                href={post.link} target="_blank" rel="noopener noreferrer"
                sx={{ fontFamily: font, textTransform: "none", fontSize: "0.75rem" }}
              >
                View Original
              </Button>
            )}
            {post.screenshotUrl && post.screenshotUrl !== "n/a" && (
              <Button variant="outlined" size="small" startIcon={<ImageIcon />}
                href={post.screenshotUrl} target="_blank" rel="noopener noreferrer"
                sx={{ fontFamily: font, textTransform: "none", fontSize: "0.75rem" }}
              >
                Screenshot
              </Button>
            )}
          </Box>
        )}

        {/* ── Engagement metrics ── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 2, borderTop: "1px solid #f1f5f9" }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton size="small" sx={{ color: "#ef4444" }}><FavoriteIcon fontSize="small" /></IconButton>
              <Typography variant="caption" sx={{ color: "#64748b", fontFamily: font }}>{post.likes || "0"}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton size="small" sx={{ color: "#3b82f6" }}><CommentIcon fontSize="small" /></IconButton>
              <Typography variant="caption" sx={{ color: "#64748b", fontFamily: font }}>{post.replies || post?.comments?.length || "0"}</Typography>
            </Box>
            {pKey !== "reddit" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton size="small" sx={{ color: "#10b981" }}>
                  {pKey === "youtube" ? <VisibilityIcon fontSize="small" /> : <RepeatIcon fontSize="small" />}
                </IconButton>
                <Typography variant="caption" sx={{ color: "#64748b", fontFamily: font }}>
                  {pKey === "youtube" ? (post.views || "0") : (post.retweets || "0")}
                </Typography>
              </Box>
            )}
            {post.languages && (
              <Chip label={post.languages} size="small"
                sx={{ fontSize: "0.625rem", fontWeight: 600, color: "#9CA3AF", bgcolor: "#F3F4F6", fontFamily: font, textTransform: "uppercase", letterSpacing: "0.6px", height: 20 }}
              />
            )}
          </Box>

          {/* Right: analysis status + creator */}
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: font, display: "block" }}>
              Added by: {post.createdBy?.username || "Unknown"}
            </Typography>
            {/* PostCard-specific: analysis done badge */}
            {/* <Chip
              label={post.analysis_done ? "✓ Analysed" : "Pending"}
              size="small"
              sx={{
                fontSize: "0.625rem", fontWeight: 700, fontFamily: font,
                bgcolor: post.analysis_done ? "rgba(16,185,129,0.08)" : "rgba(245,158,11,0.08)",
                color:   post.analysis_done ? "#059669" : "#D97706",
                border:  `1px solid ${post.analysis_done ? "rgba(16,185,129,0.2)" : "rgba(245,158,11,0.2)"}`,
                height: 20,
              }}
            /> */}
          </Box>
        </Box>

        {/* ── Media indicator ── */}
        {post.mediaPresent && post.mediaPresent !== "n/a" && (
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <ImageIcon sx={{ color: "#8b5cf6", fontSize: "1rem" }} />
            <Typography variant="caption" sx={{ color: "#8b5cf6", fontFamily: font, fontWeight: 500 }}>
              The original post contains an image or video.
            </Typography>
          </Box>
        )}

        {/* ── Translation toggle (PostCard-specific) ── */}
        {hasTranslation && (
          <Box sx={{ borderTop: "1px solid #f1f5f9", pt: 1.5, mt: 1.5 }}>
            <Box
              component="button"
              onClick={() => setShowTranslation((v) => !v)}
              sx={{ border: "none", background: "none", cursor: "pointer", fontSize: "0.75rem", fontWeight: 700, color: "#94a3b8", fontFamily: font, p: 0, mb: showTranslation ? 1 : 0, display: "block" }}
            >
              {showTranslation ? "▲ Hide" : `▼ Translation (${post.languages})`}
            </Box>
            {showTranslation && (
              <Box sx={{ fontSize: "0.8125rem", color: "#4B5563", lineHeight: 1.6, whiteSpace: "pre-wrap", bgcolor: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.12)", borderRadius: "8px", p: 1.5, fontFamily: font }}>
                {translationText}
              </Box>
            )}
          </Box>
        )}

      </CardContent>
      </Box>
    </Card>
  );
});
